import React, { useEffect, useMemo, useState, useContext } from "react";
import InputDropdown from "../CommonComponents/InputDropdown";
import CalendarHeatmap from "react-calendar-heatmap";
import "react-calendar-heatmap/dist/styles.css";
import AlertContext from "../../context/AlertContext";
import constants from "../../constants";
import useAxiosPrivate from "../../Hooks/useAxiosPrivate";
import { useParams } from  "react-router-dom"
// Helper function to generate all dates between start and end
const generateAllDates = (start, end) => {
  const dates = [];
  const currentDate = new Date(start);
  const endDate = new Date(end);

  while (currentDate <= endDate) {
    // build a local YYYY-MM-DD string so that timezone offsets don't shift the day
    const yyyy = currentDate.getFullYear();
    const mm = String(currentDate.getMonth() + 1).padStart(2, "0");
    const dd = String(currentDate.getDate()).padStart(2, "0");
    const formattedDate = `${yyyy}-${mm}-${dd}`;

    dates.push({
      date: formattedDate,
      count: 0,
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

// Merge actual data with all dates
const createCompleteData = (startDate, endDate, actualData) => {
  const allDates = generateAllDates(startDate, endDate);
  
  // Create a map of actual data for quick lookup
  const dataMap = new Map(
    actualData.map(item => [item.date, item])
  );
  
  // Merge: use actual data if exists, otherwise use zero-count placeholder
  return allDates.map(dateItem => {
    const actualItem = dataMap.get(dateItem.date);
    return actualItem || dateItem;
  });
};

const START_YEAR = new Date().getFullYear() - 2;
const CURRENT_YEAR = new Date().getFullYear();

const PublicHeatMap = () => {
    const { setShowNotify } = useContext(AlertContext);
    const axios = useAxiosPrivate();
    const { idHash, username } = useParams();
    // ✅ Generate years dynamically from 2022 → Current Year
    const heatMapOptions = useMemo(() => {
        const years = [];
        for (let year = START_YEAR; year <= CURRENT_YEAR; year++) {
            if (year === CURRENT_YEAR) years.push({ label: "Current", value: year });
            else years.push({ label: year.toString(), value: year });
        }
        return years;
    }, []);

    // ✅ Default selected year = current year
    const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
    const [data, setData] = useState([]);

    useEffect(() => {
            // Fetch data for the selected year (server expects ?year=YYYY)
        const fetchData = async () => {
            try {
                const response = await axios.get(`/node/public/u/${idHash}/${username}/heatmap?year=${selectedYear}`);
                if (response.data?.resultCode === constants.RESULT_STATUS.SUCCESS) {
                    setData(response.data.data || []);
                } else {
                    setData([]);
                }
            } catch (error) {
                console.error("Error fetching heatmap data:", error);
                setData([]);
                setShowNotify({
                    show: true,
                    title: "Error !",
                    msg: error.message || "Unable to load heatmap data. Please try again later or contact gradious team.",
                });
            }
        };

        fetchData();
    }, [selectedYear]);

    const totalSubmissions = useMemo(() => {
        return data.reduce((sum, d) => sum + d.count, 0);
    }, [data]);

    const startDate = new Date(`${selectedYear -1}-12-31`);
    const endDate = new Date(`${selectedYear}-12-31`);
    const completeData = createCompleteData(startDate, endDate, data);

    // tooltip state for custom hover display
    const [tooltip, setTooltip] = useState({ visible: false, content: "", x: 0, y: 0 });

    const handleMouseOver = (event, value) => {
        if (!value || !value.date) {
            setTooltip(prev => ({ ...prev, visible: false }));
            return;
        }
        const count = value.count || 0;
        const text = count === 0
            ? `No submissions on ${value.date}`
            : `${count} submission${count > 1 ? 's' : ''} on ${value.date}`;

        // position relative to heatmap container
        const containerRect = event.currentTarget.closest('.publicProfileHeatMapContainer').getBoundingClientRect();
        let tx = event.clientX - containerRect.left + 10;
        let ty = event.clientY - containerRect.top + 10;
        // keep tooltip inside viewport relative to container
        const tooltipWidth = 200; // match CSS max-width
        if (tx + tooltipWidth > containerRect.width) {
            tx = event.clientX - containerRect.left - tooltipWidth - 10;
        }
        const tooltipHeight = 40;
        if (ty + tooltipHeight > containerRect.height) {
            ty = event.clientY - containerRect.top - tooltipHeight - 10;
        }
        setTooltip({ visible: true, content: text, x: tx, y: ty });
    };

    const handleMouseOut = () => {
        setTooltip(prev => ({ ...prev, visible: false }));
    };

    return (
        <div className="publicProfileHeatMapContainer">
            <div className="header">
                <div className="total">
                    {totalSubmissions} Submissions
                </div>

                <InputDropdown
                    options={heatMapOptions}
                    value={selectedYear}
                    onChange={setSelectedYear}
                    className="dropdown"
                    searchable
                    placeholder="Year"
                />
            </div>

            <div className="heatmap-wrapper">
                <div className="heatmap-body">
                    {/* Weekday Labels */}
                    <div className="weekday-labels">
                        <span></span>
                        <span>Mon</span>
                        <span></span>
                        <span>Wed</span>
                        <span></span>
                        <span>Fri</span>
                        <span></span>
                    </div>
                    <div className="heatmap-grid">
                        <CalendarHeatmap
                            startDate={startDate}
                            endDate={endDate}
                            values={completeData}
                            classForValue={(value) => {
                                if (!value || value.count === 0) return "color-empty";
                                if (value.count < 3) return "color-scale-1";
                                if (value.count < 6) return "color-scale-2";
                                return "color-scale-3";
                            }}
                            // default browser tooltip removed; we'll render our own
                            titleForValue={() => ""}
                            transformDayElement={(rect, value) => {
                                // add mouse events to each day square
                                return React.cloneElement(rect, {
                                    onMouseEnter: (e) => handleMouseOver(e, value),
                                    onMouseLeave: handleMouseOut,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="legend">
                <span>Less</span>
                <div className="legend-box empty"></div>
                <div className="legend-box light"></div>
                <div className="legend-box medium"></div>
                <div className="legend-box dark"></div>
                <span>More</span>
            </div>
            {/* custom tooltip element */}
            <div
                className={`p-hm-tooltip ${tooltip.visible ? 'visible' : 'hidden'}`}
                style={{ top: tooltip.y, left: tooltip.x }}
            >
                {tooltip.content}
            </div>
        </div>
    );
};

export default PublicHeatMap;